import React, { useEffect, useState } from 'react'
import type { MarketplaceBid, MarketplaceFarm, MarketplaceJob, MarketplaceQuote } from '../../../services/backendService'
import { BidTable } from './BidTable'
import { QuoteTable } from './QuoteTable'
import { StarRating } from './StarRating'
import { loadQualityPhotos, saveQualityPhotos, type QualityPhotosRecord } from '../utils/qualityPhotos'
import '../styles/JobDetails.css'
import '../styles/FarmActiveJobsList.css'

interface JobDetailsProps {
  job: MarketplaceJob
  farms: MarketplaceFarm[]
  bids: MarketplaceBid[]
  quotes: MarketplaceQuote[]
  isLoadingOffers?: boolean
  actionError?: string | null
  selectingOfferId?: string | null
  isConfirmingDelivery?: boolean
  supportsDoctorDeliveryConfirmation?: boolean
  onBack: () => void
  onSelectBid: (bidId: string, paymentMethod?: 'card') => void
  onSelectQuote: (quoteId: string, paymentMethod?: 'card') => void
  onConfirmDelivered: (jobId: string) => void | Promise<void>
}

interface LocalMarketplaceReview {
  jobId: string
  reviewerSide: 'doctor' | 'farm'
  targetSide: 'farm' | 'doctor'
  targetName: string
  rating: number
  comment: string
  createdAt: string
}

const ACTIVE_WORKFLOW_STATUSES = new Set(['in_progress', 'printing', 'quality_check', 'shipped', 'delivered', 'completed'])
const PAYMENT_METHODS = ['card'] as const
type PaymentMethod = (typeof PAYMENT_METHODS)[number]

interface PendingOrderSelection {
  selectionType: 'bid' | 'quote'
  offerId: string
  farmName: string
  amount: number
}

const formatStatus = (status?: string | null) => (status || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, match => match.toUpperCase())
const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—')
const formatCurrency = (value?: number | null) => (typeof value === 'number'
  ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
  : '—')
const isPaymentMethod = (value?: string | null): value is PaymentMethod => PAYMENT_METHODS.includes((value || '') as PaymentMethod)

const getPaymentMethodLabel = (paymentMethod?: string | null) => {
  switch (paymentMethod) {
    case 'card':
      return 'Credit Card'
    default:
      return 'Not selected yet'
  }
}

const getPaymentRouteLabel = (selectionType?: string | null) => {
  switch (selectionType) {
    case 'quote':
      return 'Fixed-price quote checkout'
    case 'bid':
      return 'Accepted bid authorization'
    default:
      return 'Marketplace order'
  }
}

const getPaymentStatusMessage = (paymentStatus?: string | null, workflowStage?: string | null) => {
  switch ((paymentStatus || 'pending').toLowerCase()) {
    case 'authorized':
      return 'Payment authorization is confirmed and the selected farm can continue the active workflow.'
    case 'captured':
    case 'paid':
    case 'settled':
      return 'Payment has been captured for this marketplace order.'
    default:
      return ACTIVE_WORKFLOW_STATUSES.has(workflowStage || '')
        ? 'Payment authorization is still pending while the selected farm continues the confirmed workflow.'
        : 'Payment authorization will begin once the selected farm order is confirmed.'
  }
}

const getNextStepMessage = (stage?: string | null) => {
  switch (stage) {
    case 'in_progress':
      return 'Manufacturing preparation is underway at the selected farm.'
    case 'printing':
      return 'Your order is currently being printed.'
    case 'quality_check':
      return 'The farm is completing final quality checks before shipment.'
    case 'shipped':
      return 'Your order has shipped. Watch for delivery updates from the farm.'
    case 'delivered':
      return 'Delivery has been reported. Review the handoff details with the farm if needed.'
    case 'completed':
      return 'This marketplace order is complete.'
    default:
      return 'Waiting for the next marketplace workflow update.'
  }
}

export const JobDetails: React.FC<JobDetailsProps> = ({
  job,
  farms,
  bids,
  quotes,
  isLoadingOffers = false,
  actionError,
  selectingOfferId,
  isConfirmingDelivery = false,
  supportsDoctorDeliveryConfirmation = true,
  onBack,
  onSelectBid,
  onSelectQuote,
  onConfirmDelivered
}) => {
  const [pendingSelection, setPendingSelection] = useState<PendingOrderSelection | null>(null)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(isPaymentMethod(job.paymentMethod) ? job.paymentMethod : 'card')
  const [reviewRating, setReviewRating] = useState(0)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [reviewSavedAt, setReviewSavedAt] = useState<string | null>(null)
  const [qualityRecord, setQualityRecord] = useState<QualityPhotosRecord>(() => loadQualityPhotos(job.id))
  const selectedBid = job.selectedBidId ? bids.find(bid => bid.id === job.selectedBidId) : null
  const selectedQuote = job.selectedQuoteId ? quotes.find(quote => quote.id === job.selectedQuoteId) : null
  const selectedOffer = selectedBid
    ? {
        farmId: selectedBid.farmId,
        farmName: selectedBid.farmName,
        amount: selectedBid.bidAmount,
        estimatedDays: selectedBid.estimatedDays,
        rating: selectedBid.farmRating,
        reviewCount: selectedBid.farmReviewCount,
        notes: selectedBid.notes,
        selectionType: 'bid' as const
      }
    : selectedQuote
      ? {
          farmId: selectedQuote.farmId,
          farmName: selectedQuote.farmName,
          amount: selectedQuote.quoteAmount,
          estimatedDays: selectedQuote.estimatedDays,
          rating: selectedQuote.farmRating,
          reviewCount: selectedQuote.farmReviewCount,
          notes: selectedQuote.notes,
          selectionType: 'quote' as const
        }
      : null
  const selectedFarmProfile = selectedOffer ? farms.find(farm => farm.id === selectedOffer.farmId) || null : null
  const workflowStage = job.workflow?.currentStage || job.status
  const workflowTimestamps = job.workflow?.timestamps
  const selectedAmount = typeof job.selectedAmount === 'number'
    ? job.selectedAmount
    : selectedOffer?.amount ?? job.fixedPrice ?? null
  const paymentStatusKey = (job.paymentStatus || (job.selectedFarmId || selectedOffer ? 'pending' : 'unknown')).toLowerCase()
  const selectedFarmName = selectedOffer?.farmName || selectedFarmProfile?.name || (job.selectedFarmId ? 'Assigned marketplace farm' : 'Not selected yet')
  const showPaymentSummary = Boolean(job.selectedFarmId || selectedOffer || typeof selectedAmount === 'number')
  const showWorkflowTimeline = Boolean(selectedOffer || job.selectedFarmId || ACTIVE_WORKFLOW_STATUSES.has(workflowStage))
  const workflowHeadline = selectedOffer
    ? `Your order with ${selectedOffer.farmName} is confirmed. Track progress and next steps below.`
    : 'Track the current manufacturing status and next steps for this marketplace order.'
  const hasOffers = bids.length > 0 || quotes.length > 0
  const emptyStateMessage = job.pricingModel === 'fixed_price'
    ? 'No quotes yet. Farms will appear here once they respond to this fixed-price job.'
    : 'No bids yet. Farms will appear here once they respond to this job.'
  const canConfirmDelivered = workflowStage === 'shipped' && Boolean(selectedOffer)
  const canReviewFarm = (workflowStage === 'delivered' || workflowStage === 'completed') && Boolean(selectedOffer)
  const showQualityCheckForDoctor = workflowStage === 'quality_check' && Boolean(selectedOffer)
  const hasFarmPhotos = qualityRecord.photos.length >= 3
  const photosApproved = qualityRecord.approved
  const canSaveReview = reviewRating > 0 && reviewComment.trim().length > 0

  useEffect(() => {
    setPendingSelection(null)
    setPaymentMethod(isPaymentMethod(job.paymentMethod) ? job.paymentMethod : 'card')
  }, [job.id, job.paymentMethod, job.selectedBidId, job.selectedQuoteId])

  useEffect(() => {
    const storageKey = 'marketplace-reviews'
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) {
      setReviewSavedAt(null)
      setReviewComment('')
      setReviewRating(5)
      return
    }

    try {
      const parsed = JSON.parse(raw) as LocalMarketplaceReview[]
      const saved = parsed.find(review => review.jobId === job.id && review.reviewerSide === 'doctor')
      if (!saved) {
        setReviewSavedAt(null)
        setReviewComment('')
        setReviewRating(0)
        return
      }

      setReviewRating(saved.rating)
      setReviewComment(saved.comment)
      setReviewSavedAt(saved.createdAt)
    } catch {
      setReviewSavedAt(null)
    }
  }, [job.id])

  useEffect(() => {
    setQualityRecord(loadQualityPhotos(job.id))
  }, [job.id, workflowStage])

  const refreshQualityRecord = () => setQualityRecord(loadQualityPhotos(job.id))

  const handleApprovePhotos = () => {
    const next: QualityPhotosRecord = {
      ...qualityRecord,
      approved: true,
      approvedAt: new Date().toISOString(),
      rejectedAt: null
    }
    saveQualityPhotos(job.id, next)
    setQualityRecord(next)
  }

  const handleRejectPhotos = () => {
    const next: QualityPhotosRecord = {
      photos: [],
      approved: false,
      approvedAt: null,
      rejectedAt: new Date().toISOString()
    }
    saveQualityPhotos(job.id, next)
    setQualityRecord(next)
  }

  const openSelectionConfirmation = (selectionType: 'bid' | 'quote', offerId: string) => {
    const bidOffer = selectionType === 'bid'
      ? bids.find(candidate => candidate.id === offerId)
      : null
    const quoteOffer = selectionType === 'quote'
      ? quotes.find(candidate => candidate.id === offerId)
      : null
    const offer = bidOffer || quoteOffer

    if (!offer) {
      return
    }

    setPaymentMethod(isPaymentMethod(job.paymentMethod) ? job.paymentMethod : 'card')
    setPendingSelection({
      selectionType,
      offerId,
      farmName: offer.farmName,
      amount: selectionType === 'bid'
        ? (bidOffer?.bidAmount ?? 0)
        : (quoteOffer?.quoteAmount ?? 0)
    })
  }

  const handleConfirmOrder = () => {
    if (!pendingSelection) {
      return
    }

    if (pendingSelection.selectionType === 'bid') {
      onSelectBid(pendingSelection.offerId, paymentMethod)
    } else {
      onSelectQuote(pendingSelection.offerId, paymentMethod)
    }

    setPendingSelection(null)
  }

  const handleSaveReview = () => {
    if (!selectedOffer) {
      return
    }

    if (reviewRating <= 0) {
      setReviewError('Pick a star rating before saving your review.')
      return
    }

    const comment = reviewComment.trim()
    if (!comment) {
      setReviewError('Add a comment before saving your review.')
      return
    }

    setReviewError(null)
    const storageKey = 'marketplace-reviews'
    let reviews: LocalMarketplaceReview[] = []
    const existing = window.localStorage.getItem(storageKey)

    if (existing) {
      try {
        const parsed = JSON.parse(existing) as unknown
        reviews = Array.isArray(parsed) ? parsed as LocalMarketplaceReview[] : []
      } catch {
        // Recover from malformed storage so Save Review still works.
        reviews = []
      }
    }

    const nextReview: LocalMarketplaceReview = {
      jobId: job.id,
      reviewerSide: 'doctor',
      targetSide: 'farm',
      targetName: selectedOffer.farmName,
      rating: reviewRating,
      comment,
      createdAt: new Date().toISOString()
    }

    const withoutCurrent = reviews.filter(review => !(review.jobId === job.id && review.reviewerSide === 'doctor'))
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([nextReview, ...withoutCurrent]))
      setReviewSavedAt(nextReview.createdAt)
    } catch {
      setReviewError('Unable to save review right now. Please try again.')
    }
  }

  return (
    <div className="job-details">
      <button className="btn-back" onClick={onBack}>
        ← Back to Jobs
      </button>

      <div className="job-details-header">
        <div className="job-info">
          <h2>{job.title}</h2>
          <p className="description">{job.description}</p>

          <div className="specs-grid">
            <div className="spec-item">
              <span className="label">Material</span>
              <span className="value">{job.material}</span>
            </div>
            <div className="spec-item">
              <span className="label">Quality Level</span>
              <span className="value">{job.qualityLevel}</span>
            </div>
            <div className="spec-item">
              <span className="label">Deadline</span>
              <span className="value">{job.deadline}</span>
            </div>
            <div className="spec-item">
              <span className="label">STL File</span>
              <span className="value">{job.stlFileName}</span>
            </div>
            <div className="spec-item">
              <span className="label">Pricing Model</span>
              <span className="value">{job.pricingModel === 'bid' ? 'Bid System' : 'Fixed Price'}</span>
            </div>
            <div className="spec-item">
              <span className="label">Status</span>
              <span className="value">{job.status.replace('_', ' ')}</span>
            </div>
            {job.profileSummary?.name && (
              <div className="spec-item">
                <span className="label">Shared Preset</span>
                <span className="value">{job.profileSummary.name}</span>
              </div>
            )}
          </div>

          {job.pricingModel === 'fixed_price' && typeof job.fixedPrice === 'number' && (
            <div className="job-status-note">Fixed price target: ${job.fixedPrice.toFixed(2)}</div>
          )}
        </div>

        <div className="job-preview">
          <div className="stl-preview-placeholder">
            <p>STL Preview</p>
            <p className="filename">{job.stlFileName}</p>
          </div>
        </div>
      </div>

      {actionError && <div className="details-error">{actionError}</div>}

      {selectedOffer && (
        <div className="job-selection-summary">
          <div className="job-selection-summary-header">
            <div>
              <h3>Selected Farm</h3>
              <p>
                <strong>{selectedOffer.farmName}</strong> selected via{' '}
                {selectedOffer.selectionType === 'quote' ? 'fixed-price quote' : 'bid'}.
              </p>
            </div>
            <div className="job-selection-rating" aria-label="Selected farm rating">
              <span className="job-selection-rating-value">⭐ {selectedOffer.rating.toFixed(1)}</span>
              <span className="job-selection-rating-count">{selectedOffer.reviewCount} reviews</span>
            </div>
          </div>
          <p>
            ${selectedOffer.amount.toFixed(2)} · {selectedOffer.estimatedDays} day{selectedOffer.estimatedDays === 1 ? '' : 's'} estimated delivery
          </p>

          {selectedFarmProfile && (
            <div className="job-selection-profile-grid">
              <div className="job-selection-profile-card">
                <span className="label">Location</span>
                <span className="value">{selectedFarmProfile.location}</span>
              </div>

              <div className="job-selection-profile-card">
                <span className="label">Marketplace Status</span>
                <span className="value">{selectedFarmProfile.verified ? 'Verified marketplace farm' : 'Profile pending verification'}</span>
              </div>

              {selectedFarmProfile.specialties.length > 0 && (
                <div className="job-selection-profile-card">
                  <span className="label">Specialties</span>
                  <span className="value">{selectedFarmProfile.specialties.join(', ')}</span>
                </div>
              )}

              {selectedFarmProfile.materials.length > 0 && (
                <div className="job-selection-profile-card">
                  <span className="label">Materials</span>
                  <span className="value">{selectedFarmProfile.materials.join(', ')}</span>
                </div>
              )}

              {typeof selectedFarmProfile.averageTurnaroundDays === 'number' && (
                <div className="job-selection-profile-card">
                  <span className="label">Avg. Turnaround</span>
                  <span className="value">{selectedFarmProfile.averageTurnaroundDays} day{selectedFarmProfile.averageTurnaroundDays === 1 ? '' : 's'} (production only, shipping excluded)</span>
                </div>
              )}

              {selectedFarmProfile.medicalGradeCapable && (
                <div className="job-selection-profile-card">
                  <span className="label">Compliance</span>
                  <span className="value">Medical-grade capable</span>
                </div>
              )}
            </div>
          )}

          {selectedOffer.notes && (
            <p className="job-selection-offer-note">
              <strong>Offer notes:</strong> {selectedOffer.notes}
            </p>
          )}
        </div>
      )}

      {showPaymentSummary && (
        <div className="job-payment-summary" aria-label="Payment authorization">
          <div className="job-payment-header">
            <div>
              <h3>Payment & Authorization</h3>
              <p>{getPaymentStatusMessage(job.paymentStatus, workflowStage)}</p>
            </div>
            <span className={`job-payment-status status-${paymentStatusKey}`}>{formatStatus(paymentStatusKey)}</span>
          </div>

          <div className="job-payment-grid">
            <div>
              <strong>Authorized Amount</strong>
              <span>{formatCurrency(selectedAmount)}</span>
            </div>
            <div>
              <strong>Payment Route</strong>
              <span>{getPaymentRouteLabel(job.selectionType)}</span>
            </div>
            <div>
              <strong>Selected Farm</strong>
              <span>{selectedFarmName}</span>
            </div>
            <div>
              <strong>Payment Method</strong>
              <span>{getPaymentMethodLabel(job.paymentMethod)}</span>
            </div>
            <div>
              <strong>Workflow Link</strong>
              <span>{ACTIVE_WORKFLOW_STATUSES.has(workflowStage) ? 'Tracks manufacturing progress through delivery.' : 'Activates once the farm begins production updates.'}</span>
            </div>
          </div>
        </div>
      )}

      {!selectedOffer && pendingSelection && (
        <div className="job-order-confirmation" aria-label="Order confirmation">
          <div className="job-order-confirmation-header">
            <div>
              <h3>Order Confirmation</h3>
              <p>Confirm the selected marketplace offer and choose how this order should be authorized before production begins.</p>
            </div>
            <span className="job-order-confirmation-badge">Pending confirmation</span>
          </div>

          <div className="job-order-confirmation-grid">
            <div>
              <strong>Selected Farm</strong>
              <span>{pendingSelection.farmName}</span>
            </div>
            <div>
              <strong>Offer Total</strong>
              <span>{formatCurrency(pendingSelection.amount)}</span>
            </div>
            <div>
              <strong>Selection Type</strong>
              <span>{pendingSelection.selectionType === 'bid' ? 'Accepted bid' : 'Fixed-price quote'}</span>
            </div>
          </div>

          <fieldset className="job-order-payment-methods">
            <legend>Payment Method</legend>
            <label>
              <input type="radio" name="paymentMethod" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
              Credit Card
            </label>
          </fieldset>

          <div className="job-order-confirmation-actions">
            <button type="button" className="btn-secondary" onClick={() => setPendingSelection(null)} disabled={Boolean(selectingOfferId)}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={handleConfirmOrder} disabled={Boolean(selectingOfferId)}>
              {selectingOfferId ? 'Confirming…' : 'Confirm Order'}
            </button>
          </div>
        </div>
      )}

      {showWorkflowTimeline && (
        <div className="job-workflow-summary" aria-label="Order timeline">
          <div className="job-workflow-header">
            <div>
              <h3>Order Timeline</h3>
              <p>{workflowHeadline}</p>
            </div>
            <span className={`job-workflow-stage stage-${workflowStage}`}>{formatStatus(workflowStage)}</span>
          </div>

          <div className="job-workflow-grid">
            <div>
              <strong>Current Stage</strong>
              <span>{formatStatus(workflowStage)}</span>
            </div>
            <div>
              <strong>Job Submitted</strong>
              <span>{formatDateTime(workflowTimestamps?.createdAt || job.createdAt)}</span>
            </div>
            <div>
              <strong>Farm Selected</strong>
              <span>{formatDateTime(workflowTimestamps?.acceptedAt)}</span>
            </div>
            <div>
              <strong>Last Updated</strong>
              <span>{formatDateTime(workflowTimestamps?.updatedAt || job.updatedAt)}</span>
            </div>
            <div>
              <strong>Completed At</strong>
              <span>{formatDateTime(workflowTimestamps?.closedAt || job.closedAt)}</span>
            </div>
          </div>

          <div className="job-workflow-next-step" aria-label="Order next steps">
            <strong>Next Step</strong>
            <span>{getNextStepMessage(workflowStage)}</span>
          </div>

          {showQualityCheckForDoctor && (
            <div className="job-workflow-next-step" aria-label="Quality check photos">
              <strong>Quality Check Photos</strong>
              {hasFarmPhotos ? (
                <>
                  <span>The farm submitted 3 photos of the final product. Approve to allow shipment.</span>
                  <div className="quality-photo-gallery" aria-label="Quality check photo gallery">
                    {qualityRecord.photos.map((photo, index) => (
                      photo.dataUrl ? (
                        <img
                          key={`${photo.name}-${index}`}
                          src={photo.dataUrl}
                          alt={`Quality check photo ${index + 1}`}
                          className="quality-photo-thumb"
                        />
                      ) : (
                        <div key={`${photo.name}-${index}`} className="quality-photo-thumb is-placeholder">{photo.name}</div>
                      )
                    ))}
                  </div>
                  {photosApproved ? (
                    <span className="quality-photo-status is-approved">Approved on {qualityRecord.approvedAt ? new Date(qualityRecord.approvedAt).toLocaleString() : '—'}</span>
                  ) : (
                    <div className="quality-photo-actions">
                      <button type="button" className="btn-primary" onClick={handleApprovePhotos}>Approve Photos</button>
                      <button type="button" className="btn-secondary" onClick={handleRejectPhotos}>Reject &amp; Request Re-upload</button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <span>Waiting for the farm to upload 3 quality check photos before shipment.</span>
                  <button type="button" className="btn-secondary" onClick={refreshQualityRecord} style={{ marginTop: '8px', width: 'fit-content' }}>Refresh</button>
                </>
              )}
            </div>
          )}

          {canConfirmDelivered && supportsDoctorDeliveryConfirmation && (
            <div className="job-workflow-next-step" aria-label="Delivery confirmation">
              <strong>Delivery Confirmation</strong>
              <span>The farm marked this order as shipped. Confirm delivery after the package arrives.</span>
              <button
                type="button"
                className="btn-primary"
                onClick={() => onConfirmDelivered(job.id)}
                disabled={isConfirmingDelivery}
                style={{ marginTop: '10px', width: 'fit-content' }}
              >
                {isConfirmingDelivery ? 'Confirming...' : 'Mark As Delivered'}
              </button>
            </div>
          )}

          {canConfirmDelivered && !supportsDoctorDeliveryConfirmation && (
            <div className="job-workflow-next-step" aria-label="Delivery confirmation unavailable">
              <strong>Delivery Confirmation</strong>
              <span>Doctor-side delivery confirmation is not available on this backend deployment yet. Deploy the backend version that includes the confirm-delivered endpoint.</span>
            </div>
          )}

          {canReviewFarm && (
            <div className="job-workflow-next-step" aria-label="Farm review">
              <strong>Review Farm</strong>
              <span>Rate the production experience and leave a comment for this completed job.</span>
              <div style={{ marginTop: '10px' }}>
                <StarRating value={reviewRating} onChange={setReviewRating} ariaLabel="Farm rating" />
              </div>
              <textarea
                value={reviewComment}
                onChange={(event) => setReviewComment(event.target.value)}
                placeholder="Write your review comment"
                rows={3}
                style={{ marginTop: '10px' }}
              />
              {reviewError && <div className="details-error" style={{ marginTop: '8px' }}>{reviewError}</div>}
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveReview}
                disabled={!canSaveReview}
                style={{ marginTop: '10px', width: 'fit-content' }}
              >
                Save Review
              </button>
              {reviewSavedAt && <span style={{ display: 'block', marginTop: '8px' }}>Saved on {new Date(reviewSavedAt).toLocaleString()}</span>}
            </div>
          )}
        </div>
      )}

      {isLoadingOffers ? (
        <div className="no-bids-placeholder">
          <p>Loading offers...</p>
        </div>
      ) : (
        <>
          {bids.length > 0 && (
            <div className="bids-section">
              <h3>Farm Bids ({bids.length})</h3>
              <BidTable bids={bids} acceptingBidId={selectingOfferId} onAcceptBid={(bidId) => openSelectionConfirmation('bid', bidId)} />
            </div>
          )}

          {quotes.length > 0 && (
            <div className="quotes-section">
              <h3>Farm Quotes ({quotes.length})</h3>
              <QuoteTable quotes={quotes} selectingQuoteId={selectingOfferId} onSelectQuote={(quoteId) => openSelectionConfirmation('quote', quoteId)} />
            </div>
          )}

          {!hasOffers && (
            <div className="no-bids-placeholder">
              <p>{emptyStateMessage}</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

